/**
 * Third-party app integrations for the product form.
 *
 * Nothing in the theme depends on this file. It exists so app-specific selectors and quirks stay
 * out of `product-form.js`, which knows only the generic contract: set `data-add-to-cart-blocked`
 * on a `product-form-component` and call `refreshAddToCartAvailability()` to block purchase; remove
 * it and call again to hand control back to the variant's real availability.
 *
 * If the apps below are uninstalled, delete this file and its `<script>` tag in
 * `snippets/scripts.liquid`. Nothing else needs to change.
 *
 * ## Essential Preorder
 *
 * Mounts a "notify me when back in stock" signup inside the product form for sold-out variants.
 * Three things make it awkward:
 *
 * 1. It replaces the buy area without disabling add-to-cart, so the button can stay live under an
 *    inapplicable form.
 * 2. It deactivates that container in place rather than removing it, so "the container exists" is
 *    not the same as "the signup is showing". A previous fix keyed on presence alone, via custom
 *    CSS in `settings_data.json`, which left add-to-cart permanently greyed and unclickable after
 *    any sold-out selection.
 * 3. Morph / the app can strip `disabled` *after* we set `data-add-to-cart-blocked`. Refresh must
 *    therefore re-run whenever the signup is showing, not only when the attribute flips.
 */

import { ThemeEvents } from '@theme/events';

const BLOCKED_ATTRIBUTE = 'data-add-to-cart-blocked';

const BACK_IN_STOCK_SELECTOR = '[data-essential-preorder-element="back-in-stock-container"]';
const BACK_IN_STOCK_ACTIVE_CLASS = 'essential-preorder-bis-container-active';

/** @typedef {HTMLElement & { refreshAddToCartAvailability?: () => void }} ProductForm */

/** @type {WeakSet<HTMLElement>} */
const observedForms = new WeakSet();

/** @type {WeakMap<HTMLElement, number>} */
const pendingFrames = new WeakMap();

/**
 * Whether an element is actually painted, so a deactivated container cannot be mistaken for a live
 * one. `checkVisibility` covers `display`/`visibility`/`opacity`/`content-visibility` across the
 * whole ancestor chain; `offsetParent` is the fallback for browsers without it.
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isRendered(element) {
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility({
      contentVisibilityAuto: true,
      opacityProperty: true,
      visibilityProperty: true,
    });
  }

  return element.offsetParent !== null;
}

/**
 * Whether a back-in-stock signup is currently the active buy-area UI in this form.
 * @param {HTMLElement} form
 * @returns {boolean}
 */
function isBackInStockSignupShowing(form) {
  const container = form.querySelector(BACK_IN_STOCK_SELECTOR);
  if (!(container instanceof HTMLElement)) return false;
  if (!isRendered(container)) return false;
  if (container.classList.contains(BACK_IN_STOCK_ACTIVE_CLASS)) return true;

  // Deactivated containers are emptied rather than removed, so they collapse to zero height.
  return container.getBoundingClientRect().height > 0;
}

/**
 * @param {ProductForm} form
 */
function syncBlockedState(form) {
  const shouldBlock = isBackInStockSignupShowing(form);
  const wasBlocked = form.hasAttribute(BLOCKED_ATTRIBUTE);

  if (shouldBlock && !wasBlocked) {
    form.setAttribute(BLOCKED_ATTRIBUTE, '');
  } else if (!shouldBlock && wasBlocked) {
    form.removeAttribute(BLOCKED_ATTRIBUTE);
  }

  // While BIS is showing, always re-assert — not only when the attribute flips. Morph and the app
  // can strip `disabled` after we set `data-add-to-cart-blocked`, which left ATC clickable with
  // the block flag still set (confirmed: a manual refreshAddToCartAvailability() fixed it instantly).
  // When idle and already unblocked, skip so unrelated mutations don't poke the product form.
  if (shouldBlock || shouldBlock !== wasBlocked) {
    form.refreshAddToCartAvailability?.();
  }
}

/**
 * Coalesces a burst of app mutations into one measured pass per frame, since reading layout is the
 * expensive half of {@link isBackInStockSignupShowing}.
 * @param {ProductForm} form
 */
function scheduleSync(form) {
  if (pendingFrames.has(form)) return;

  pendingFrames.set(
    form,
    requestAnimationFrame(() => {
      pendingFrames.delete(form);
      if (form.isConnected) syncBlockedState(form);
    })
  );
}

/**
 * Apps mount and toggle their buy-area UI after first paint without firing theme events, so watch
 * the subtree rather than assuming a single injection point or moment.
 * @param {ProductForm} form
 */
function observe(form) {
  if (observedForms.has(form)) return;
  observedForms.add(form);

  new MutationObserver(() => scheduleSync(form)).observe(form, {
    childList: true,
    subtree: true,
    attributes: true,
    // Include `disabled`: the product form writes it, but the app/morph can clear it afterwards.
    // Re-assert is safe — #syncAddToCartAvailability only writes when the value actually changes.
    attributeFilter: ['class', 'style', 'hidden', 'disabled'],
  });

  syncBlockedState(form);
}

function observeAll() {
  for (const form of document.querySelectorAll('product-form-component')) {
    if (form instanceof HTMLElement) observe(form);
  }
}

/** Re-syncs every product form, and starts observing any that appeared since last time. */
function scheduleSyncAll() {
  observeAll();

  for (const form of document.querySelectorAll('product-form-component')) {
    if (form instanceof HTMLElement) scheduleSync(form);
  }
}

// Wait for the upgrade rather than assuming this module runs after product-form.js: an
// unupgraded <product-form-component> has no refreshAddToCartAvailability() to call.
customElements.whenDefined('product-form-component').then(() => {
  observeAll();

  // Forms rendered later — a quick-add dialog, or a combined listing swapping the whole section —
  // are picked up on the next variant event. Also re-sync already-observed forms: variant morph
  // can clear `disabled` in the same turn the block attribute is already set.
  document.addEventListener(ThemeEvents.variantUpdate, scheduleSyncAll);
});
